import type { ComponentProps, ReactNode } from 'react';
import { cn } from './cn';

const tones = {
  neutral: 'bg-crow-raised text-crow-text-2',
  /** Active, selected, belongs to you. */
  accent: 'bg-crow-accent-tint text-crow-accent-ink',
  /** Information, metadata, what comes from others: the companion colour. */
  info: 'bg-crow-companion-tint text-crow-companion-ink',
  success: 'bg-[color-mix(in_oklch,var(--crow-success)_12%,var(--crow-surface))] text-crow-success',
  warning: 'bg-[color-mix(in_oklch,var(--crow-warning)_14%,var(--crow-surface))] text-crow-warning',
  danger: 'bg-crow-danger-tint text-crow-danger',
} as const;

export type BadgeTone = keyof typeof tones;

/** A short label. Colour is never alone: the text says what it means. */
export function Badge({
  tone = 'neutral',
  live = false,
  children,
  className,
  ...props
}: Omit<ComponentProps<'span'>, 'children' | 'className'> & {
  tone?: BadgeTone;
  /** A breathing dot, for work in progress or a live room. The only looping animation allowed. */
  live?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      {...props}
      className={cn(
        'inline-flex h-[22px] items-center gap-1.5 rounded-[5px] px-2 text-crow-label font-semibold whitespace-nowrap',
        tones[tone],
        className,
      )}
    >
      {live ? (
        <span aria-hidden className="size-1.5 animate-crow-breathe rounded-full bg-current" />
      ) : null}
      {children}
    </span>
  );
}
